import {createSyncQueue} from '../offline/queue.js';
import {SyncStatus} from '../offline/entity.js';
import {createSmokehouseAnalytics} from './analytics.js';
import {createSmokehouseDashboardModel} from './dashboard-model.js';
import {createSmokehouseOfflineStore} from './offline.js';
import {createSmokehouseSyncProcessor} from './sync.js';
import {createSmokehouseOnboardingFlow} from './onboarding-flow.js';
import {createSmokerCapabilityStore} from './smoker-capability-store.js';
import {createSmokerFlow} from './smoker-create-flow.js';
import {createSmokerStore,SMOKER_ENTITY_TYPE} from './smoker-store.js';
import {smokerPhotoPlaceholder} from './smoker-photo.js';
import {createThermometerStore} from './thermometer-store.js';
import {createProbeStore} from './probe-store.js';
import {createThermometerFlow} from './thermometer-flow.js';
import {createWoodFavouriteStore} from './wood-favourites.js';
import {createWoodInventoryLocalService} from './wood-inventory-service.js';
import {createWoodInventoryFlow} from './wood-inventory-flow.js';
import {createWoodCatalogueService} from './wood-catalogue-service.js';
import {createTastePreferences} from './taste-preferences.js';

function requiredUserId(value){
  const userId=String(value||'').trim();
  if(!userId)throw new TypeError('userId is required');
  return userId;
}

export function createSmokehouseRuntime({
  repository,
  userId,
  analytics=null,
  diagnostics=null,
  remoteAdapters=null,
  defaultRemote=null,
  photoStorage=null,
  woodCatalogueRemote=null,
  now=()=>new Date().toISOString(),
  isOnline=()=>globalThis.navigator?.onLine!==false
}={}){
  if(!repository?.get||!repository?.put||!repository?.list||!repository?.listPending){
    throw new TypeError('Smokehouse repository is required');
  }
  const owner=requiredUserId(userId);
  const queue=createSyncQueue({repository,now});
  const adoption=analytics?createSmokehouseAnalytics({analytics}):null;
  const offline=createSmokehouseOfflineStore({repository,queue,userId:owner,now});
  const smokers=createSmokerStore({repository,queue,now});
  const capabilities=createSmokerCapabilityStore({repository,queue,smokerStore:smokers,now});
  const thermometers=createThermometerStore({repository,queue,now});
  const probes=createProbeStore({repository,queue,thermometerStore:thermometers,now});
  const thermometerFlow=createThermometerFlow({store:thermometers,isOnline});
  const wood=createWoodInventoryLocalService({repository,queue,userId:owner,now});
  const woodFlow=createWoodInventoryFlow({service:wood,isOnline});
  const woodCatalogue=createWoodCatalogueService({
    repository,
    remote:woodCatalogueRemote,
    diagnostics,
    isOnline
  });
  const favourites=createWoodFavouriteStore({repository,queue,now});
  const onboarding=createSmokehouseOnboardingFlow({
    store:offline,
    analytics:adoption,
    now
  });
  const smokerFlow=createSmokerFlow({
    smokerStore:smokers,
    capabilityStore:capabilities,
    isOnline
  });
  const resolvedRemote=remoteAdapters;
  function getPhotoStorage(){
    return photoStorage;
  }
  const sync=resolvedRemote?createSmokehouseSyncProcessor({
    repository,
    queue,
    remoteAdapters:resolvedRemote
  }):null;

  async function syncPending(){
    if(!sync)return Object.freeze({ok:false,status:'remote_unavailable'});
    if(!isOnline())return Object.freeze({ok:false,status:'offline'});
    const result=await sync.processPending();
    return Object.freeze({ok:true,status:'processed',...result});
  }

  async function dashboard(){
    const [profile,progress,smokerRows,thermometerRows,woodRows]=await Promise.all([
      offline.getProfile(),
      offline.getOnboarding(),
      smokers.list({userId:owner}),
      thermometers.list({userId:owner}),
      wood.list()
    ]);
    return createSmokehouseDashboardModel({
      profile,
      onboarding:progress,
      smokers:smokerRows,
      thermometers:thermometerRows,
      woodInventory:woodRows
    });
  }

  async function initialScreen(){
    const state=await onboarding.state();
    const status=state.progress.status;
    if(status!=='completed'&&status!=='skipped'){
      const progress=await onboarding.resume();
      return Object.freeze({
        kind:'onboarding',
        step:progress.currentStep,
        profile:await offline.getProfile(),
        progress
      });
    }
    return Object.freeze({kind:'dashboard',model:await dashboard()});
  }

  async function createSmoker(input={}){
    const result=await smokerFlow.save({...input,userId:owner});
    if(result.ok){
      adoption?.smokerCreated?.({
        smoker_type:input.smokerTypeKey||'unknown',
        temperature_control:input.temperatureControl||'unknown',
        cold_smoking:input.coldSmoking||'unknown',
        offline:!isOnline()
      });
      const progress=await offline.getOnboarding();
      if(progress.status==='in_progress'&&progress.currentStep==='smoker'){
        await onboarding.completeAfterSmoker();
      }
    }
    return result;
  }

  async function editSmoker(id,input={}){
    const current=await smokers.get(id);
    if(!current||current.userId!==owner)throw new TypeError('Smoker not found');
    const result=await smokerFlow.save({...input,userId:owner},{id});
    if(result.ok){
      adoption?.smokerUpdated?.({
        smoker_type:input.smokerTypeKey||current.smokerTypeKey||'unknown',
        temperature_control:input.temperatureControl||'unknown',
        cold_smoking:input.coldSmoking||'unknown',
        offline:!isOnline()
      });
    }
    return result;
  }

  async function setDefaultSmoker(id){
    const current=await smokers.get(id);
    if(!current||current.userId!==owner)throw new TypeError('Smoker not found');

    const before=await smokers.list({userId:owner});
    const beforeStored=await Promise.all(before.map(smoker=>smokers.get(smoker.id)));
    const remoteEligible=Boolean(
      defaultRemote
      &&isOnline()
      &&before.length
      &&beforeStored.every(smoker=>
        smoker
        &&smoker.syncStatus===SyncStatus.SYNCED
        &&Number(smoker.serverVersion)>0
      )
    );

    const updated=await smokers.setDefault(id);
    const active=await smokers.list({userId:owner});
    adoption?.defaultSmokerChanged?.(active.length);

    if(updated.length===0){
      return Object.freeze({ok:true,status:'unchanged',updated});
    }

    if(!remoteEligible){
      return Object.freeze({
        ok:true,
        status:isOnline()?'saved_pending_sync':'saved_offline',
        updated
      });
    }

    try{
      const remoteRows=await defaultRemote.setDefault(id);
      const remoteById=new Map(remoteRows.map(row=>[row.id,row]));

      for(const local of before){
        const remote=remoteById.get(local.id);
        if(!remote)throw new Error('DEFAULT_SMOKER_REMOTE_INCOMPLETE');
        const stored=await smokers.get(local.id);
        if(!stored)continue;
        await repository.put(SMOKER_ENTITY_TYPE,{
          ...stored,
          isDefault:remote.isDefault,
          serverVersion:remote.serverVersion,
          syncStatus:SyncStatus.SYNCED,
          syncAttempts:0,
          conflict:null
        });
      }

      return Object.freeze({
        ok:true,
        status:'saved_synced',
        updated:Object.freeze(await smokers.list({userId:owner}))
      });
    }catch(error){
      try{diagnostics?.capture?.('smokehouse.default-smoker-sync',error);}catch{}
      return Object.freeze({
        ok:true,
        status:'saved_pending_sync',
        updated,
        remoteErrorCode:String(error?.code||error?.message||'DEFAULT_SMOKER_REMOTE_FAILED').slice(0,80)
      });
    }
  }

  async function planSmokerArchive(id,{historicalSessionCount=0}={}){
    const current=await smokers.get(id);
    if(!current||current.userId!==owner)throw new TypeError('Smoker not found');
    return smokers.planArchive(id,{historicalSessionCount});
  }

  async function archiveSmoker(id,{replacementDefaultId=null,historicalSessionCount=0}={}){
    const current=await smokers.get(id);
    if(!current||current.userId!==owner)throw new TypeError('Smoker not found');
    const result=await smokers.archive(id,{replacementDefaultId,historicalSessionCount});
    if(result.ok){
      adoption?.smokerArchived?.({
        smoker_type:current.smokerTypeKey||'unknown',
        offline:!isOnline()
      });
    }
    return result;
  }

  async function replaceSmokerPhoto(id,{file,mimeType,sizeBytes,localPreviewUri=null}={}){
    const current=await smokers.get(id);
    if(!current||current.userId!==owner)throw new TypeError('Smoker not found');

    if(!isOnline()){
      return Object.freeze({
        ok:false,
        status:'offline',
        placeholder:smokerPhotoPlaceholder({
          hasLocalPreview:Boolean(localPreviewUri),
          isOffline:true
        }),
        localPreviewUri:localPreviewUri?String(localPreviewUri):null
      });
    }
    const storage=getPhotoStorage();
    if(!storage){
      return Object.freeze({ok:false,status:'storage_unavailable',placeholder:'empty_placeholder'});
    }

    const uploaded=await storage.upload({
      userId:owner,
      smokerId:id,
      currentPath:current.photoPath,
      file,
      mimeType,
      sizeBytes,
      localPreviewUri
    });

    let saved;
    try{
      saved=await smokers.edit(id,{photoPath:uploaded.path});
    }catch(error){
      if(uploaded.path&&uploaded.path!==current.photoPath){
        try{await storage.cleanup(uploaded.path);}catch(cleanupError){
          try{diagnostics?.capture?.('smokehouse.photo-cleanup',cleanupError);}catch{}
        }
      }
      throw error;
    }

    let cleanupPending=false;
    if(uploaded.deleteAfterUpload){
      try{await storage.cleanup(uploaded.deleteAfterUpload);}
      catch(error){
        cleanupPending=true;
        try{diagnostics?.capture?.('smokehouse.photo-cleanup',error);}catch{}
      }
    }

    return Object.freeze({
      ok:true,
      status:'saved_pending_sync',
      path:uploaded.path,
      cleanupPending,
      smoker:saved
    });
  }

  async function removeSmokerPhoto(id){
    const current=await smokers.get(id);
    if(!current||current.userId!==owner)throw new TypeError('Smoker not found');
    if(!current.photoPath)return Object.freeze({ok:true,status:'already_absent'});
    if(!isOnline())return Object.freeze({ok:false,status:'offline',placeholder:'offline_placeholder'});
    const storage=getPhotoStorage();
    if(!storage)return Object.freeze({ok:false,status:'storage_unavailable'});

    const previous=current.photoPath;
    const saved=await smokers.edit(id,{photoPath:null});
    try{
      await storage.remove({userId:owner,smokerId:id,currentPath:previous});
      return Object.freeze({ok:true,status:'saved_pending_sync',smoker:saved});
    }catch(error){
      try{diagnostics?.capture?.('smokehouse.photo-delete',error);}catch{}
      return Object.freeze({
        ok:true,
        status:'saved_pending_sync',
        cleanupPending:true,
        smoker:saved,
        errorCode:String(error?.code||error?.message||'SMOKER_PHOTO_DELETE_FAILED').slice(0,80)
      });
    }
  }

  async function smokerPhotoUrl(id){
    const current=await smokers.get(id);
    if(!current||current.userId!==owner)throw new TypeError('Smoker not found');
    if(!current.photoPath||!isOnline())return null;
    const storage=getPhotoStorage();
    if(!storage)return null;
    return storage.signedUrl({
      userId:owner,
      smokerId:id,
      path:current.photoPath
    });
  }

  async function saveThermometer(input={},options={}){
    const result=await thermometerFlow.save({...input,userId:owner},options);
    if(result.ok){
      const properties={
        thermometer_type:input.typeKey||result.saved?.typeKey||'other',
        channel_count:input.channelCount??result.saved?.channelCount??0,
        offline:!isOnline()
      };
      if(options?.id)adoption?.thermometerUpdated?.(properties);
      else adoption?.thermometerCreated?.(properties);
    }
    return result;
  }

  async function archiveThermometer(id){
    const saved=await thermometers.archive(id);
    adoption?.thermometerArchived?.({offline:!isOnline()});
    return saved;
  }

  async function saveWood(input={},options={}){
    const result=await woodFlow.save(input,options);
    if(result.ok){
      const properties={
        wood_source:input.customName?'custom':'catalogue',
        has_quantity:input.quantity!=null&&input.quantity!=='',
        quantity_unit:input.quantityUnit||'unknown',
        offline:!isOnline()
      };
      if(options?.id)adoption?.woodInventoryUpdated?.(properties);
      else adoption?.woodInventoryAdded?.(properties);
    }
    return result;
  }

  async function woodFavouriteKeys(){
    const rows=await favourites.list(owner);
    return Object.freeze(rows.map(item=>item.woodKey));
  }

  async function setWoodFavourite(woodKey,desired=true){
    const key=String(woodKey||'').trim().toLowerCase();
    if(!key)throw new TypeError('woodKey is required');
    const wanted=Boolean(desired);
    const current=await favourites.isFavourite(owner,key);
    if(current===wanted){
      return Object.freeze({
        ok:true,
        status:'unchanged',
        woodKey:key,
        favourite:wanted
      });
    }

    if(wanted){
      await favourites.add(owner,key);
      adoption?.woodFavouriteAdded?.();
    }else{
      await favourites.remove(owner,key);
      adoption?.woodFavouriteRemoved?.();
    }

    return Object.freeze({
      ok:true,
      status:isOnline()?'saved_pending_sync':'saved_offline',
      woodKey:key,
      favourite:wanted
    });
  }

  async function archiveWood(id){
    const result=await woodFlow.archive(id);
    if(result.ok)adoption?.woodInventoryArchived?.({offline:!isOnline()});
    return result;
  }

  async function saveTastePreferences(preferences){
    const values=createTastePreferences(preferences);
    return offline.saveProfile({tastePreferences:values});
  }

  async function summary(){
    return Object.freeze({
      dashboard:await dashboard(),
      pending:await offline.pendingSummary()
    });
  }

  return Object.freeze({
    userId:owner,
    queue,
    offline,
    smokers,
    capabilities,
    thermometers,
    probes,
    thermometerFlow,
    wood,
    woodFlow,
    woodCatalogue,
    favourites,
    onboarding,
    smokerFlow,
    sync,
    syncPending,
    initialScreen,
    createSmoker,
    editSmoker,
    setDefaultSmoker,
    planSmokerArchive,
    archiveSmoker,
    replaceSmokerPhoto,
    removeSmokerPhoto,
    smokerPhotoUrl,
    saveThermometer,
    archiveThermometer,
    saveWood,
    woodFavouriteKeys,
    setWoodFavourite,
    archiveWood,
    saveTastePreferences,
    dashboard,
    summary
  });
}
